import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { RevenueDailyMetric, RevenueStatsType } from 'types/analytics'
import { gqlQuery } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'
import type { VariablesOf } from '@graphql-typed-document-node/core'
import { graphql } from 'app/generated/gql'

const REVENUE_METRICS_QUERY = graphql(`
  query FinancialPageMetrics(
    $dateRange: DateRangeInput!
    $compareRange: DateRangeInput
    $locationIds: [String!]
    $integrationStatus: String
  ) {
    revenueMetrics(
      dateRange: $dateRange
      compareRange: $compareRange
      locationIds: $locationIds
      integrationStatus: $integrationStatus
    ) {
      stats {
        grossProduction
        netProduction
        adjustments
        collections
        avgGrossProductionPerVisit
        avgNetProductionPerVisit
        avgAdjustmentsPerVisit
      }
      comparisonStats {
        grossProduction
        netProduction
        adjustments
        collections
        avgGrossProductionPerVisit
        avgNetProductionPerVisit
        avgAdjustmentsPerVisit
      }
      dailyMetrics {
        bucket
        grossProduction
        netProduction
        collections
      }
      comparisonDailyMetrics {
        bucket
        grossProduction
        netProduction
        collections
      }
    }
  }
`)

function useRevenueMetrics(
  fromDate: string,
  toDate: string,
  compareFromDate: string,
  compareToDate: string,
  dontCompare: boolean,
  locationIds: string[],
  integrationStatus?: string,
): [RevenueStatsType | undefined, RevenueStatsType | undefined, boolean, RevenueDailyMetric[], RevenueDailyMetric[]] {
  const [stats, setStats] = useState<RevenueStatsType>()
  const [comparisonStats, setComparisonStats] = useState<RevenueStatsType>()
  const [dailyMetrics, setDailyMetrics] = useState<RevenueDailyMetric[]>([])
  const [comparisonDailyMetrics, setComparisonDailyMetrics] = useState<RevenueDailyMetric[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const locationIdsKey = locationIds.join(`,`)

  useEffect(() => {
    if (!fromDate || !toDate) return
    setIsLoading(true)
    const compareRange =
      !dontCompare && compareFromDate && compareToDate
        ? { fromDate: compareFromDate, toDate: compareToDate }
        : undefined
    const variables: VariablesOf<typeof REVENUE_METRICS_QUERY> = {
      dateRange: { fromDate, toDate },
      compareRange,
      locationIds: locationIds.length ? locationIds : undefined,
      integrationStatus: integrationStatus || undefined,
    }
    gqlQuery(REVENUE_METRICS_QUERY, variables)
      .then(({ data, error }) => {
        if (error) {
          logError(`Error fetching revenue metrics`, error.message)
          return
        }
        if (data?.revenueMetrics) {
          setStats(data.revenueMetrics.stats)
          setComparisonStats(data.revenueMetrics.comparisonStats ?? undefined)
          setDailyMetrics(data.revenueMetrics.dailyMetrics ?? [])
          setComparisonDailyMetrics(data.revenueMetrics.comparisonDailyMetrics ?? [])
        }
      })
      .catch((err) => logError(`Error fetching revenue metrics`, err))
      .finally(() => setIsLoading(false))
  }, [fromDate, toDate, compareFromDate, compareToDate, dontCompare, locationIdsKey, integrationStatus])

  return [stats, comparisonStats, isLoading, dailyMetrics, comparisonDailyMetrics]
}

export type UseFinancialPageReturn = {
  openTab: string
  setOpenTab: (tab: string) => void
  locationIds: string[]
  dateRange: { fromDate: string; toDate: string; compareFromDate: string; compareToDate: string; dontCompare: boolean }
  revenueStats: RevenueStatsType | undefined
  comparisonRevenueStats: RevenueStatsType | undefined
  isLoading: boolean
  dailyMetrics: RevenueDailyMetric[]
  comparisonDailyMetrics: RevenueDailyMetric[]
  dateChangeHandler: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  hideCompare: boolean
}

export function useFinancialPage(): UseFinancialPageReturn {
  const [searchParams] = useSearchParams()
  const hideCompare = useFeatureToggle(`revenue-dashboard`)
  const [openTab, setOpenTab] = useState<string>(`Summary`)
  const locationIds = useMemo(() => compact(searchParams.get(`locationIds`)?.split(`,`) ?? []), [searchParams])
  const [dateRange, setDateRange] = useState({
    fromDate: searchParams.get(`fromDate`) ?? ``,
    toDate: searchParams.get(`toDate`) ?? ``,
    compareFromDate: searchParams.get(`compareFromDate`) ?? ``,
    compareToDate: searchParams.get(`compareToDate`) ?? ``,
    dontCompare: searchParams.get(`dontCompare`) === `true`,
  })
  const integrationStatus = useMemo(() => searchParams.get(`integrationStatus`) ?? ``, [searchParams])
  const [revenueStats, comparisonRevenueStats, isLoading, dailyMetrics, comparisonDailyMetrics] = useRevenueMetrics(
    dateRange.fromDate,
    dateRange.toDate,
    dateRange.compareFromDate,
    dateRange.compareToDate,
    dateRange.dontCompare,
    locationIds,
    integrationStatus,
  )

  const dateChangeHandler = (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => {
    setDateRange({ fromDate, toDate, compareFromDate, compareToDate, dontCompare })
  }

  return {
    openTab,
    setOpenTab,
    locationIds,
    dateRange,
    revenueStats,
    comparisonRevenueStats,
    isLoading,
    dailyMetrics,
    comparisonDailyMetrics,
    dateChangeHandler,
    hideCompare,
  }
}
